import React from "react"
import { PrimeButton } from "components-design-system"
import { Panel } from "components-design-system"
import { Typography, Flex } from "antd"
import ReactCountryFlag from "react-country-flag"
import EditCountriesModal from "../EditCountriesModal/EditCountriesModal"
import { useGetCountries } from "api/hooks/enum"
import { countries as countriesConstant } from "constants/countries"
import "./CountriesPanel.scss"

const { Title } = Typography

const NO_FLAG_SET = new Set(["YU", "S1"])

const CountriesPanel = ({ permissions, actualCountries, plannedCountries, studyId, siteList, isSitesLoading }) => {
  const { data: countries = countriesConstant, isLoading: isCountriesLoading } = useGetCountries()
  const [showModal, setShowModal] = React.useState(false)
  const [modalVariant, setModalVariant] = React.useState("actual")

  const toggleModal = (variant) => {
    setShowModal((state) => !state)
    setModalVariant(variant)
  }

  const actions = React.useMemo(() => {
    const ret = []

    if (permissions?.["study.edit"] === true) {
      ret.push(
        <PrimeButton
          onClick={() => toggleModal("planned")}
          disabled={isSitesLoading || siteList?.length > 0}
          tooltip={
            siteList?.length > 0 &&
            "This study includes site data as specified in the table below. In such cases, the Planned and Actual Country values are automatically set based on the countries and recruitment status of the sites. To edit these values, either update the site data or delete all sites to re-enable editing of the Planned and Actual Country fields."
          }
        >
          Edit Planned
        </PrimeButton>
      )
    }

    if (permissions?.["study.edit"] === true) {
      ret.push(
        <PrimeButton
          onClick={() => toggleModal("actual")}
          disabled={isSitesLoading || siteList?.length > 0}
          tooltip={
            siteList?.length > 0 &&
            "This study includes site data as specified in the table below. In such cases, the Planned and Actual Country values are automatically set based on the countries and recruitment status of the sites. To edit these values, either update the site data or delete all sites to re-enable editing of the Planned and Actual Country fields."
          }
        >
          Edit Actual
        </PrimeButton>
      )
    }

    return ret
  }, [permissions, siteList, isSitesLoading])

  if (!actualCountries || !plannedCountries || isCountriesLoading) {
    return
  }

  return (
    <Panel
      title="COUNTRIES"
      iconClassName="fa-light fa-globe"
      actions={actions}
      className={`countries-panel`}
    >
      <Flex justify="space-between">
        <div className="countries-panel-col">
          <Flex className="countries-panel-col-head">
            <Title
              level={4}
              className="countries-panel-label"
            >
              Planned
            </Title>
          </Flex>
          <div className="flags-container">
            {plannedCountries
              ?.filter((item) => item)
              .map((code) => {
                let country = countries?.[code]

                return (
                  <div
                    key={`countries-panel-${code}`}
                    className="countries-panel-flag-wrap"
                  >
                    {!NO_FLAG_SET.has(code) && (
                      <ReactCountryFlag
                        key={country}
                        svg
                        countryCode={code}
                        className="country-flag"
                      />
                    )}
                    <p className="countries-panel-label">{country?.label}</p>
                  </div>
                )
              })}
          </div>
        </div>
        <div className="countries-panel-col">
          <Flex className="countries-panel-col-head">
            <Title
              level={4}
              className="countries-panel-label"
            >
              Actual
            </Title>
          </Flex>
          <div className="flags-container">
            {actualCountries
              ?.filter((item) => item)
              .map((code) => {
                let country = countries?.[code]
                return (
                  <div
                    key={`countries-panel-${code}`}
                    className="countries-panel-flag-wrap"
                  >
                    {!NO_FLAG_SET.has(code) && (
                      <ReactCountryFlag
                        key={code}
                        svg
                        countryCode={code}
                        className="country-flag"
                      />
                    )}
                    <p className="countries-panel-label">{country?.label}</p>
                  </div>
                )
              })}
          </div>
        </div>
      </Flex>
      {permissions?.["study.edit"] === true && countries && (
        <EditCountriesModal
          showModal={showModal}
          onHide={toggleModal}
          variant={modalVariant}
          countryCodes={modalVariant === "planned" ? plannedCountries : actualCountries}
          countries={countries}
          studyId={studyId}
        />
      )}
    </Panel>
  )
}

export default CountriesPanel
